import { BaseVault__factory, getProvider } from '@optimex-xyz/trade-wallet-sdk'

import { NATIVE_TOKEN_ADDRESS } from '@/constants'
import { IToken } from '@/types'

import { ethers, JsonRpcProvider } from 'ethers'

import { BaseTransactionOptions, TransactionHelper, TransactionTracker, withRetry } from './transactionHelper'

import { isEvmChain } from '../chain'

interface TransactionResult {
  txId: string | null
  lastCheckedBlock: number
}

interface TransactionParams {
  networkId: string
  tradeId: string
  fromAddress: string
  toAddress: string
  tokenAddress?: string
  blockStartNumber?: number | null
  blocksToScan?: number
}

interface EvmTransactionOptions extends BaseTransactionOptions {
  networkId: string
  toAddress: string
  amount: number | bigint | string
  tradeId: string
  fromAddress: string
  tokenAddress?: string
  sendEvmFn: (toAddress: string, amount: any, options?: any) => Promise<string>
  options?: any
}

async function getTxId({
  networkId,
  fromAddress,
  toAddress,
  tradeId,
  tokenAddress,
  blockStartNumber: blockNumber = null,
  blocksToScan = 5000,
}: TransactionParams): Promise<TransactionResult> {
  const provider = getProvider(networkId)

  try {
    if (!ethers.isAddress(fromAddress) || !ethers.isAddress(toAddress)) {
      throw new Error('Invalid address')
    }

    const normalizedFromAddress = fromAddress.toLowerCase()

    const contract = BaseVault__factory.connect(toAddress, provider)

    const currentBlock = await provider.getBlockNumber()

    let startBlock: number
    if (blockNumber !== null) {
      startBlock = blockNumber
    } else {
      startBlock = currentBlock
    }

    const endBlock = Math.min(startBlock + blocksToScan, currentBlock)

    const depositFilter = contract.filters.Deposited(
      tradeId,
      normalizedFromAddress,
      tokenAddress === 'native' ? '0x0000000000000000000000000000000000000000' : tokenAddress
    )

    const depositLogs = await contract.queryFilter(depositFilter, startBlock, endBlock)

    let firstTxId: string | null = null

    if (depositLogs.length > 0) {
      const firstDeposit = depositLogs[0]
      firstTxId = firstDeposit.transactionHash
    }

    return {
      txId: firstTxId,
      lastCheckedBlock: endBlock,
    }
  } catch (error) {
    console.error('Error while retrieving transaction:', error)
    throw error
  }
}

class EvmTransactionTracker implements TransactionTracker {
  private lastCheckedBlock: number | null = null

  constructor(
    private readonly options: {
      networkId: string
      fromAddress: string
      toAddress: string
      tradeId: string
      tokenAddress?: string
    }
  ) {}

  async trackTransaction(controller: AbortController): Promise<string | null> {
    const provider = getProvider(this.options.networkId)
    const initialBlock = await provider.getBlockNumber()

    return withRetry(
      async () => {
        const result = await getTxId({
          ...this.options,
          blockStartNumber: this.lastCheckedBlock || initialBlock - 10,
          blocksToScan: 20,
        })

        this.lastCheckedBlock = result.lastCheckedBlock

        return result.txId
      },
      {
        controller,
        retryDelay: 5000,
        initialDelay: 1000,
        maxRetries: 120,
      }
    )
  }
}

class EvmTransactionHelper extends TransactionHelper<EvmTransactionOptions> {
  protected createTracker(options: EvmTransactionOptions): TransactionTracker {
    return new EvmTransactionTracker({
      networkId: options.networkId,
      fromAddress: options.fromAddress,
      toAddress: options.toAddress,
      tradeId: options.tradeId,
      tokenAddress: options.tokenAddress,
    })
  }

  protected sendTransaction(options: EvmTransactionOptions): Promise<string> {
    return options.sendEvmFn(options.toAddress, options.amount, options.options)
  }

  protected getNetworkId(): string {
    return this.options?.networkId || ''
  }
}

export async function handleEvmTransaction(options: EvmTransactionOptions): Promise<string> {
  const helper = new EvmTransactionHelper(options)
  return helper.handleTransaction(options)
}

export const isNativeToken = (token: IToken): boolean => {
  return token?.tokenAddress === NATIVE_TOKEN_ADDRESS
}

export const isValidErc20Token = (token: IToken): boolean => {
  return isEvmChain(token.networkId) && !isNativeToken(token)
}

export async function getBlockTimestamp(provider: JsonRpcProvider): Promise<number> {
  const block = await provider.getBlockNumber()
  const timestamp = (await provider.getBlock(block))?.timestamp || 0
  return timestamp
}
